/**
 * src/lib/canvas.js
 *
 * All canvas compositing logic for Blockamatic.
 * Extracted from the main component so it can be
 * shared between the web app and future Capacitor builds.
 */

// ─── Grain ────────────────────────────────────────────────────────────────────

export function applyGrain(ctx, w, h, intensity) {
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const noise = (Math.random() - 0.5) * intensity * 255;
    data[i]     += noise;
    data[i + 1] += noise;
    data[i + 2] += noise;
  }
  ctx.putImageData(imageData, 0, 0);
}

// ─── Vignette ─────────────────────────────────────────────────────────────────

export function applyVignette(ctx, w, h, strength) {
  const gradient = ctx.createRadialGradient(
    w / 2, h / 2, h * 0.3,
    w / 2, h / 2, h * 0.85
  );
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(1, `rgba(0,0,0,${strength})`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
}

// ─── Block stamp ──────────────────────────────────────────────────────────────

export function drawStamp(ctx, w, h, blockHeight, style, previewScale = 1) {
  const text     = `BLOCK ${blockHeight}`;
  const liveStampPx = 14;
  const fontSize = Math.max(14, Math.round(liveStampPx * previewScale));

  ctx.save();
  ctx.font          = `bold ${fontSize}px 'Share Tech Mono', 'DS-Digital', 'Digital-7 Mono', 'Courier New', monospace`;
  ctx.textAlign     = "right";
  ctx.textBaseline  = "bottom";
  ctx.fillStyle     = style.stampColor;
  ctx.shadowColor   = style.stampGlow;

  const x = w - w * 0.04;
  const y = h - h * 0.05;

  // Glow passes
  ctx.shadowBlur = 10;
  for (let i = 0; i < 2; i++) ctx.fillText(text, x, y);

  // Sharp pass
  ctx.shadowBlur = 2;
  ctx.fillText(text, x, y);

  ctx.restore();
}

function applyTriXFallback(ctx, w, h) {
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    // Perceptual grayscale.
    let y = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Contrast + slight brightness trim to match Tri-X character.
    y = (y - 128) * 1.3 + 128;
    y = y * 0.95;
    y = Math.max(0, Math.min(255, y));

    data[i] = y;
    data[i + 1] = y;
    data[i + 2] = y;
  }

  ctx.putImageData(imageData, 0, 0);
}

// ─── Full composite ───────────────────────────────────────────────────────────

export async function compositePhoto(videoEl, style, blockHeight, isLandscape = true) {
  const sourceW = videoEl.videoWidth  || 1280;
  const sourceH = videoEl.videoHeight || 720;
  const targetRatio = isLandscape ? 3 / 2 : 2 / 3;

  let sx = 0;
  let sy = 0;
  let sw = sourceW;
  let sh = sourceH;

  if (sourceW / sourceH > targetRatio) {
    sw = Math.round(sourceH * targetRatio);
    sx = Math.round((sourceW - sw) / 2);
  } else if (sourceW / sourceH < targetRatio) {
    sh = Math.round(sourceW / targetRatio);
    sy = Math.round((sourceH - sh) / 2);
  }

  const displayW = videoEl.clientWidth || sw;
  const displayH = videoEl.clientHeight || sh;
  const frameW = Math.min(displayW, displayH * targetRatio);
  const frameH = frameW / targetRatio;
  const previewScale = Math.min(sw / frameW, sh / frameH);

  const canvas = document.createElement("canvas");
  canvas.width  = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d");

  // Draw 3:2 crop from source frame first.
  ctx.drawImage(videoEl, sx, sy, sw, sh, 0, 0, sw, sh);

  // Apply style filter in a second canvas pass for better cross-browser
  // reliability (some engines ignore ctx.filter on direct video draws).
  if (style.filter && style.filter !== "none") {
    const filteredCanvas = document.createElement("canvas");
    filteredCanvas.width = sw;
    filteredCanvas.height = sh;
    const filteredCtx = filteredCanvas.getContext("2d");
    filteredCtx.filter = style.filter;
    filteredCtx.drawImage(canvas, 0, 0, sw, sh);
    filteredCtx.filter = "none";

    ctx.clearRect(0, 0, sw, sh);
    ctx.drawImage(filteredCanvas, 0, 0, sw, sh);
  }

  // Some browsers/devices inconsistently apply canvas filters to video frames.
  // Keep Tri-X deterministic by enforcing a monochrome fallback on capture.
  if (style.id === "trix") applyTriXFallback(ctx, sw, sh);

  // Grain
  if (style.grain > 0)    applyGrain(ctx, sw, sh, style.grain);

  // Vignette
  if (style.vignette > 0) applyVignette(ctx, sw, sh, style.vignette);

  // Stamp
  drawStamp(ctx, sw, sh, blockHeight, style, previewScale);

  return canvas.toDataURL("image/jpeg", 0.92);
}
